import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
// core
import Tooltip from '@material-ui/core/Tooltip';
import Zoom from '@material-ui/core/Zoom';
import Button from '@material-ui/core/Button';
import { withStyles, styled, ThemeProvider } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Badge from '@material-ui/core/Badge';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
// icons
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import DashboardIcon from '@material-ui/icons/DashboardOutlined';
import AssignmentIcon from '@material-ui/icons/AssignmentOutlined';
import AssessmentIcon from '@material-ui/icons/AssessmentOutlined';
import AccountTreeIcon from '@material-ui/icons/AccountTreeOutlined';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOnOutlined';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccountOutlined';
import SettingsIcon from '@material-ui/icons/SettingsOutlined';
import ExitToAppIcon from '@material-ui/icons/ExitToAppOutlined';
import StoreIcon from '@material-ui/icons/StoreOutlined';
import CategoryIcon from '@material-ui/icons/CategoryOutlined';
import BallotIcon from '@material-ui/icons/BallotOutlined';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import SaveIcon from '@material-ui/icons/SaveOutlined';
import DescriptionIcon from '@material-ui/icons/Description';
import GroupsIcon from '@material-ui/icons/GroupWorkOutlined';
import ApartmentIcon from '@material-ui/icons/Apartment';
import DonutLargeIcon from '@material-ui/icons/DonutLarge';
import ProductInfoIcon from '@material-ui/icons/ListAltOutlined';
import NotesIcon from '@material-ui/icons/Notes';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import LinkIcon from '@material-ui/icons/Link';

import useStyles from './styles';
import { CLIENT_EMPLOYEE, SUPER_ADMIN, TELEAUDIT } from '../../../utils/const/user';
import authService from '../../../services/authService';
import darkTheme from '../../../views/themeDark';

const DrawerTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.custom.sidebarTooltipBackground,
    color: theme.palette.custom.sidebarTooltipText,
    textAlign: 'center',
    width: '95px',
    maxWidth: '100%',
    fontSize: '1em',
  },
  arrow: {
    // Was `primary.main`, which never matched the tooltip's own background.
    color: theme.palette.custom.sidebarTooltipBackground,
    border: '0px',
  },
}))(Tooltip);

export default function MiniDrawer(props) {
  const sidebarTheme = darkTheme;

  return (
    <ThemeProvider theme={sidebarTheme}>
      <MiniDrawerContent {...props} />
    </ThemeProvider>
  );
}

function MiniDrawerContent({
  companies, handleCompanyChange, currentCompany, ticketsQuantity,
  searchedCompanies, searchingCompanies, handleCompanySearch,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const account = useSelector((state) => state.account);
  const role = useSelector((state) => state.account.user.role);
  const [open, setOpen] = useState(true);
  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };

  const logout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  const StyledBadge = styled(Badge)(() => ({
    '& .MuiBadge-badge': {
      right: 20,
      top: 4,
      border: '2px',
      padding: '4px',
    },
  }));

  const doIHaveAccessToCommonMenu = true;
  const doIHaveAccessToAdminMenu = role === SUPER_ADMIN;
  const doIHaveAccessToAdministrationTab = role === SUPER_ADMIN;
  const doIHaveAccessToUsersTab = role === SUPER_ADMIN;
  const doIHaveAccessToProductsTab = role === SUPER_ADMIN;
  const doIHaveAccessToLocationTab = role === SUPER_ADMIN;
  const doIHaveAccessToCategoriesTab = role === SUPER_ADMIN;
  const doIHaveAccessToGroupsTab = role === SUPER_ADMIN;
  const doIHaveAccessToTaskTab = role === SUPER_ADMIN;
  const doIHaveAccessToQATab = role === SUPER_ADMIN;
  const doIHaveAccessToTeleauditTab = role === SUPER_ADMIN || role === TELEAUDIT;
  const doIHaveAccessToCommonTeleauditTab = role === TELEAUDIT;
  const doIHaveAccessToResponsesTab = role === SUPER_ADMIN;
  const doIHaveAccessToBulkAssociationTab = role === SUPER_ADMIN;

  // INICIO ADMIN TABS
  const administrationTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.administration') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/administration"
        className={window.location.pathname === '/administration' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <ApartmentIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/administration' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('layout.tabs.administration')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const usersTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.users') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/users"
        className={window.location.pathname === '/users' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <SupervisorAccountIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/users' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('layout.tabs.users')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const productsTab = (
    <>
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.products') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/products"
          className={(window.location.pathname === '/products' || window.location.pathname === '/upload-products'
            || window.location.pathname === '/upload-products-images' || window.location.pathname.startsWith('/product-groups')) && open && classes.listItemSelected}
        >
          <ListItemIcon>
            <CategoryIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: (window.location.pathname === '/products' || window.location.pathname === '/upload-products'
                || window.location.pathname === '/upload-products-images' || window.location.pathname.startsWith('/product-groups')
              ) && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.tabs.products')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* PRODUCTS SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'Ver todos' : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/products"
          className={clsx(classes.listSubItem, window.location.pathname === '/products' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/products' || window.location.pathname === '/upload-products'
              || window.location.pathname === '/upload-products-images'
            ),
          })}
        >
          <ListItemIcon>
            <VisibilityIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/products' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.viewAll')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* PRODUCTS SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'Cargar archivo' : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/upload-products"
          className={clsx(classes.listSubItem, window.location.pathname === '/upload-products' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/products' || window.location.pathname === '/upload-products'
              || window.location.pathname === '/upload-products-images'
            ),
          })}
        >
          <ListItemIcon>
            <SaveIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/upload-products' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.import')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* PRODUCTS SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'Cargar imágenes' : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/upload-products-images"
          className={clsx(classes.listSubItem, window.location.pathname === '/upload-products-images' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/products' || window.location.pathname === '/upload-products'
              || window.location.pathname === '/upload-products-images') || window.location.pathname.startsWith('/product-groups'),
          })}
        >
          <ListItemIcon>
            <CloudUploadIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/upload-products-images' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.uploadProductsImages')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* PRODUCT GROUPS SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tabs.productGroups') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/product-groups"
          className={clsx(classes.listSubItem, window.location.pathname.startsWith('/product-groups') && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/products' || window.location.pathname === '/upload-products'
              || window.location.pathname === '/upload-products-images'
              || window.location.pathname.startsWith('/product-groups')
            ),
          })}
        >
          <ListItemIcon>
            <GroupsIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname.startsWith('/product-groups') && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.tabs.productGroups')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* PRODUCT GROUPS VIEW ALL SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.subTabs.viewAll') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/product-groups"
          className={clsx(classes.listSubItemDouble, window.location.pathname === '/product-groups' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/product-groups' || window.location.pathname === '/product-groups/bulk-upload'
              || window.location.pathname === '/product-groups/create'
            ),
          })}
        >
          <ListItemIcon>
            <VisibilityIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/product-groups' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.viewAll')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* PRODUCT GROUPS BULK UPLOAD SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.subTabs.bulkUpload') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/product-groups/bulk-upload"
          className={clsx(classes.listSubItemDouble, window.location.pathname === '/product-groups/bulk-upload' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/product-groups' || window.location.pathname === '/product-groups/bulk-upload'
              || window.location.pathname === '/product-groups/create'
            ),
          })}
        >
          <ListItemIcon>
            <SaveIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/product-groups/bulk-upload' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.bulkUpload')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* PRODUCT GROUPS CREATE SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.subTabs.create') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/product-groups/create"
          className={clsx(classes.listSubItemDouble, window.location.pathname === '/product-groups/create' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/product-groups' || window.location.pathname === '/product-groups/bulk-upload'
              || window.location.pathname === '/product-groups/create'
            ),
          })}
        >
          <ListItemIcon>
            <SaveIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/product-groups/create' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.create')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
    </>
  );
  const locationTab = (
    <>
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.locations') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/locations"
          className={(window.location.pathname === '/locations' || window.location.pathname === '/upload-locations') && open && classes.listItemSelected}
        >
          <ListItemIcon>
            <StoreIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: (window.location.pathname === '/locations' || window.location.pathname === '/upload-locations') && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.tabs.locations')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* LOCATIONS SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'Ver todos' : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/locations"
          className={clsx(classes.listSubItem, window.location.pathname === '/locations' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/locations' || window.location.pathname === '/upload-locations' || window.location.pathname === '/upload-product-locations' || window.location.pathname === '/upload-stock-and-sales-information'),
          })}
        >
          <ListItemIcon>
            <VisibilityIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/locations' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.viewAll')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* LOCATIONS SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.productsInformation') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/upload-product-locations"
          className={clsx(classes.listSubItem, window.location.pathname === '/upload-product-locations' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/locations' || window.location.pathname === '/upload-locations' || window.location.pathname === '/upload-product-locations' || window.location.pathname === '/upload-stock-and-sales-information'),
          })}
        >
          <ListItemIcon>
            <ProductInfoIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/upload-product-locations' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.productsInformation')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* LOCATIONS SUBMENU */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.stockAndSales') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/upload-stock-and-sales-information"
          className={clsx(classes.listSubItem, window.location.pathname === '/upload-stock-and-sales-information' && open && classes.listSubItemSelected, {
            [classes.hide]: !(window.location.pathname === '/locations' || window.location.pathname === '/upload-locations' || window.location.pathname === '/upload-product-locations' || window.location.pathname === '/upload-stock-and-sales-information'),
          })}
        >
          <ListItemIcon>
            <ProductInfoIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/upload-stock-and-sales-information' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.subTabs.stockAndSales')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
    </>
  );
  const categoriesTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.categories') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/categories"
        className={window.location.pathname === '/categories' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <CategoryIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/categories' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('layout.tabs.categories')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const groupsTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.groups') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/groups"
        className={window.location.pathname === '/groups' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <GroupsIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/groups' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('layout.tabs.groups')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const taskTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.tasks') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/tasks"
        className={(window.location.pathname === '/tasks') && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <BallotIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: (window.location.pathname === '/tasks') && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('layout.tabs.tasks')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const qaTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'QA' : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/qa"
        className={window.location.pathname === '/qa' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <ApartmentIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/qa' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={'QA'}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const teleauditTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('teleaudit.sideBar') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/teleaudit"
        className={window.location.pathname === '/teleaudit' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <ApartmentIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/teleaudit' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('teleaudit.sideBar')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const responsesTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('responses.sideBar') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/responses"
        className={window.location.pathname === '/responses' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <NotesIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/responses' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('responses.sideBar')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  const bulkAssociationTab = (
    <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.bulkAssociation') : ''} arrow>
      <ListItem
        button
        component={Link}
        to="/bulk-association"
        className={window.location.pathname === '/bulk-association' && open && classes.listItemSelected}
      >
        <ListItemIcon>
          <LinkIcon className={clsx({
            [classes.iconOpen]: open,
            [classes.iconClose]: !open,
            [classes.iconSelected]: window.location.pathname === '/bulk-association' && !open,
          })}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('layout.tabs.bulkAssociation')}
          className={clsx({
            [classes.hide]: !open,
          })}
        />
      </ListItem>
    </DrawerTooltip>
  );
  // FIN ADMIN TABS

  const commonMenu = (
    <>
      <Divider className={clsx({
        [classes.dividerOpen]: open,
        [classes.dividerClose]: !open,
      })}
      />
      <p className={clsx(classes.tabTitle, {
        [classes.tabTitleOpen]: open,
        [classes.tabTitleClose]: !open,
      })}
      >{t('layout.tabsTitle.menu')}</p>
      {/* HOME */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.home') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/home"
          className={window.location.pathname === '/home' && open && classes.listItemSelected}
        >
          <ListItemIcon>
            <DashboardIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/home' && !open,
            })}
            /></ListItemIcon>
          <ListItemText
            primary={t('layout.tooltips.home')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* TICKETS */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.supportTickets') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/support"
          className={window.location.pathname === '/support' && open && classes.listItemSelected}
        >
          <StyledBadge
            badgeContent={ticketsQuantity}
            color='error'
            overlap="rectangular"
          >
            <ListItemIcon>
              <AssignmentIcon className={clsx({
                [classes.iconOpen]: open,
                [classes.iconClose]: !open,
                [classes.iconSelected]: window.location.pathname === '/support' && !open,
              })}
              />
            </ListItemIcon>
          </StyledBadge>
          <ListItemText
            primary={t('layout.tabs.supportTickets')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* CHALLENGES */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'Challenges' : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/challenges"
          className={window.location.pathname === '/challenges' && open && classes.listItemSelected}
        >
          <ListItemIcon>
            <DonutLargeIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/challenges' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={'Challenges'}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* BI REPORTS */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tooltips.biReports') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/reports"
          className={
            window.location.pathname.startsWith('/reports')
            && open
            && classes.listItemSelected}
        >
          <ListItemIcon>
            <AssessmentIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/reports' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.tabs.biReports')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* GANTT */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'Gantt' : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/gantts"
          className={window.location.pathname === '/gantts' && open && classes.listItemSelected}
        >
          <ListItemIcon>
            <AccountTreeIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/gantts' && !open,
            })} />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.tabs.ganttCharts')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* DOCUMENTS */}
      <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tabs.documents') : ''} arrow>
        <ListItem
          button
          component={Link}
          to="/documents"
          className={window.location.pathname === '/documents' && open && classes.listItemSelected}
        >
          <ListItemIcon>
            <DescriptionIcon className={clsx({
              [classes.iconOpen]: open,
              [classes.iconClose]: !open,
              [classes.iconSelected]: window.location.pathname === '/wallet' && !open,
            })}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('layout.tabs.documents')}
            className={clsx({
              [classes.hide]: !open,
            })}
          />
        </ListItem>
      </DrawerTooltip>
      {/* TOKENS */}
      {account.user.role === CLIENT_EMPLOYEE ? null : (
        <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? 'Tokens' : ''} arrow>
          <ListItem
            button
            component={Link}
            to="/wallet"
            className={window.location.pathname === '/wallet' && open && classes.listItemSelected}
          >
            <ListItemIcon>
              <MonetizationOnIcon className={clsx({
                [classes.iconOpen]: open,
                [classes.iconClose]: !open,
                [classes.iconSelected]: window.location.pathname === '/wallet' && !open,
              })}
              />
            </ListItemIcon>
            <ListItemText
              primary='Tokens'
              className={clsx({
                [classes.hide]: !open,
              })}
            />
          </ListItem>
        </DrawerTooltip>
      )}
      {/* TELEAUDIT */}
      {doIHaveAccessToCommonTeleauditTab && teleauditTab}
    </>
  );

  const adminMenu = (
    <>
      <Divider className={clsx({
        [classes.dividerOpen]: open,
        [classes.dividerClose]: !open,
      })}
      />
      <p className={clsx(classes.tabTitle, {
        [classes.tabTitleOpen]: open,
        [classes.tabTitleClose]: !open,
      })}
      >{'Menú Admin'}</p>
      {doIHaveAccessToAdministrationTab && administrationTab}
      {doIHaveAccessToUsersTab && usersTab}
      {doIHaveAccessToProductsTab && productsTab}
      {doIHaveAccessToLocationTab && locationTab}
      {doIHaveAccessToCategoriesTab && categoriesTab}
      {doIHaveAccessToGroupsTab && groupsTab}
      {doIHaveAccessToTaskTab && taskTab}
      {doIHaveAccessToQATab && qaTab}
      {doIHaveAccessToTeleauditTab && teleauditTab}
      {doIHaveAccessToResponsesTab && responsesTab}
      {doIHaveAccessToBulkAssociationTab && bulkAssociationTab}
    </>
  );

  return (
    <div className={classes.root}>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div className={clsx(classes.toolbar, {
          [classes.toolbarClose]: !open,
        })}>
          <div className={clsx(classes.logoContainer, {
            [classes.hide]: !open,
          })}>
            <img
              className={classes.logoLockup}
              src='/logo-lockup-dark.png'
              alt="TCG Scout"
            />
          </div>
          <Button
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.image, {
              [classes.hide]: open,
            })}
          >
            <img
              className={classes.image}
              src="/TCGHRES2021BACK2DRAW.png"
              alt="TCG logo"
            />
          </Button>
          <IconButton
            color="inherit"
            aria-label="close drawer"
            onClick={handleDrawerClose}
            edge="start"
            className={clsx(classes.menuButton, {
              [classes.hide]: !open,
            })}
          >
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <div className={classes.containerTabItems}>
          <div>
            <p className={clsx(classes.tabTitle, {
              [classes.tabTitleOpen]: open,
              [classes.tabTitleClose]: !open,
            })}
            >{t('layout.tabsTitle.company')}</p>
            <div className={classes.companySelector}>
              <Autocomplete
                options={companies}
                value={companies.find((c) => String(c.value) === String(currentCompany)) || null}
                getOptionLabel={(option) => option.name}
                getOptionSelected={(option, selected) => option.value === selected.value}
                // Server results replace local filtering while a remote search is active
                filterOptions={searchedCompanies ? () => searchedCompanies : undefined}
                loading={searchingCompanies}
                loadingText={t('layout.companySearch.loading')}
                noOptionsText={t('layout.companySearch.noOptions')}
                disableClearable
                fullWidth
                onChange={(event, option) => option && handleCompanyChange(option.value)}
                onInputChange={(event, value, reason) => {
                  handleCompanySearch(reason === 'input' ? value : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': t('layout.tabsTitle.company'),
                    }}
                  />
                )}
              />
            </div>
            <List>
              {doIHaveAccessToCommonMenu && commonMenu}
              {doIHaveAccessToAdminMenu && adminMenu}
            </List>
          </div>
          <div className={classes.bottomContainer}>
            <Divider className={clsx({
              [classes.dividerOpen]: open,
              [classes.dividerClose]: !open,
            })}
            />
            <List>
              <p className={clsx(classes.tabTitle, {
                [classes.tabTitleOpen]: open,
                [classes.tabTitleClose]: !open,
              })}>{t('layout.tabsTitle.others')}</p>
              {account.user.role === SUPER_ADMIN && (
                <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tabs.settings') : ''} arrow>
                  <ListItem
                    button
                    component={Link}
                    to="/settings"
                    disabled={account.user.role !== SUPER_ADMIN}
                    className={window.location.pathname === '/settings' && open && classes.listItemSelected}
                  >
                    <ListItemIcon>
                      <SettingsIcon className={clsx({
                        [classes.iconOpen]: open,
                        [classes.iconClose]: !open,
                        [classes.iconSelected]: window.location.pathname === '/settings' && !open,
                      })}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('layout.tabs.settings')}
                      className={clsx({
                        [classes.hide]: !open,
                      })}
                    />
                  </ListItem>

                </DrawerTooltip>
              )}
              <DrawerTooltip TransitionComponent={Zoom} placement='right' title={!open ? t('layout.tabs.logout') : ''} arrow>
                <ListItem button onClick={logout}>
                  <ListItemIcon><ExitToAppIcon className={classes.redIcon} /></ListItemIcon>
                  <ListItemText
                    primary={t('layout.tabs.logout')}
                    className={clsx({
                      [classes.hide]: !open,
                    })}
                  />
                </ListItem>
              </DrawerTooltip>
            </List>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
